import { formatSar } from "../../../utils/billingMoney";

// The AI Wallet as the Menu module shows it (balance indicator, usage after
// an AI operation, refusals). Amounts always come from the backend (wallet
// and charge); nothing here computes a price or keeps a balance of its own.

// Billing page, AI Wallet section of that restaurant, with the top-up form
// open (when the user may add funds; otherwise the section is just shown).
export const aiWalletRechargePath = (storeId) => {
  const params = new URLSearchParams();
  if (storeId) params.set("storeId", storeId);
  params.set("aiWallet", "recharge");
  return `/auth/billing?${params.toString()}#billingPanelAiWallet`;
};

// An AI operation may have changed the wallet: the indicator re-reads it.
export const AI_WALLET_CHANGED = "lud:ai-wallet-changed";
export const notifyAiWalletChanged = () => {
  try {
    window.dispatchEvent(new CustomEvent(AI_WALLET_CHANGED));
  } catch (error) {
    // no window (tests without DOM): nothing to refresh
  }
};

export const walletAmount = (minor, lang) => formatSar(minor, lang === "en" ? "en" : "ar");

const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, value), template);

const TEXT = {
  en: {
    label: "AI Wallet",
    open: "Open AI Wallet in Billing",
    low: "Low balance",
    frozen: "Frozen",
    notCharging: "AI usage is not deducted from this balance yet.",
    reserved: "{amount} reserved for operations in progress",
    used: "Used",
    remaining: "Remaining",
    notCharged: "Not charged",
    pending: "Calculated when processing ends",
    insufficientTitle: "Insufficient AI Wallet balance",
    balanceLine: "Your AI Wallet balance is {balance}.",
    requiredLine: "This operation requires {required}.",
    shortageLine: "You need {shortage} more to continue.",
    enoughNow: "The balance is now enough. You can try again.",
    available: "Available balance",
    required: "Required",
    shortage: "Needed",
    recharge: "Recharge AI Wallet",
  },
  ar: {
    label: "محفظة الذكاء الاصطناعي",
    open: "فتح محفظة الذكاء الاصطناعي في الفوترة",
    low: "رصيد منخفض",
    frozen: "موقوفة",
    notCharging: "لا يُخصم استخدام الذكاء الاصطناعي من هذا الرصيد بعد.",
    reserved: "{amount} محجوزة لعمليات قيد التنفيذ",
    used: "المستهلك",
    remaining: "المتبقي",
    notCharged: "بدون خصم",
    pending: "يُحسب عند انتهاء المعالجة",
    insufficientTitle: "رصيد محفظة الذكاء الاصطناعي غير كافٍ",
    balanceLine: "رصيد محفظة الذكاء الاصطناعي {balance}.",
    requiredLine: "تتطلب هذه العملية {required}.",
    shortageLine: "تحتاج إلى {shortage} إضافية للمتابعة.",
    enoughNow: "الرصيد أصبح كافياً. يمكنك إعادة المحاولة.",
    available: "الرصيد المتاح",
    required: "المطلوب",
    shortage: "المبلغ الإضافي",
    recharge: "شحن محفظة الذكاء الاصطناعي",
  },
};

export const aiWalletMenuText = (lang) => (lang === "en" ? TEXT.en : TEXT.ar);

// "Your AI Wallet balance is 2.40 SAR. This operation requires 5.75 SAR. You
// need 3.35 SAR more to continue." from the structured refusal; "" when the
// response has no amounts (staff without billing access).
export function shortageSentence(wallet, lang) {
  if (!wallet?.canViewBalance || !Number.isFinite(Number(wallet.requiredAmountMinor))) return "";
  const t = aiWalletMenuText(lang);
  const lines = [fill(t.balanceLine, { balance: walletAmount(wallet.availableBalanceMinor, lang) }), fill(t.requiredLine, { required: walletAmount(wallet.requiredAmountMinor, lang) })];
  lines.push(Number(wallet.shortageMinor) > 0 ? fill(t.shortageLine, { shortage: walletAmount(wallet.shortageMinor, lang) }) : t.enoughNow);
  // The Arabic currency ends with a period ("ر.س."): no second one after it.
  return lines.join(" ").replace(/\.(‏*)\./g, ".$1");
}

export const reservedText = (minor, lang) => fill(aiWalletMenuText(lang).reserved, { amount: walletAmount(minor, lang) });
